import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Building2, Plus, Pause, PlayCircle, Clock, TrendingUp, HardDrive,
    Users, Mail, XCircle, CheckCircle2, AlertTriangle,
} from "lucide-react";

/**
 * Super Admin panel — SaaS Phase 0.
 *
 * Read-first surface. Ships:
 *   • Snapshot header — total tenants, active/trial breakdown, MRR/ARR, expiring trials, storage.
 *   • Tenants table with per-row plan/status editing, trial extension, suspend/activate.
 *   • New-tenant creation dialog.
 *
 * Data isolation & billing come in Phase 1 — this panel is intentionally
 * light on writes so the ops team can observe before enforcing.
 */

const STATUS_TONE = {
    active:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    trial:     "text-blue-700    bg-blue-50    border-blue-200",
    suspended: "text-rose-700    bg-rose-50    border-rose-200",
    cancelled: "text-stone-700   bg-stone-100  border-stone-200",
};

export default function SuperAdmin() {
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({
        name: "", type: "Business", admin_name: "", admin_email: "",
        admin_mobile: "", plan: "professional", trial_days: 14,
    });
    const [reasonPrompt, setReasonPrompt] = useState(null);
    const [reason, setReason] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [s, t, p] = await Promise.all([
                api.get("/super-admin/summary"),
                api.get("/super-admin/tenants"),
                api.get("/plans"),
            ]);
            setSummary(s.data);
            setRows(t.data?.tenants || []);
            setPlans(p.data?.plans || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const createTenant = async () => {
        try {
            await api.post("/super-admin/tenants", form);
            toast.success(`Workspace created for ${form.admin_email}`);
            setCreateOpen(false);
            setForm({ ...form, name: "", admin_name: "", admin_email: "", admin_mobile: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const applyUpdate = async (tenantId, patch) => {
        try {
            await api.patch(`/super-admin/tenants/${tenantId}`, patch);
            toast.success("Tenant updated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const confirmReason = async () => {
        if (reason.trim().length < 5) {
            toast.error("Reason must be at least 5 characters.");
            return;
        }
        await applyUpdate(reasonPrompt.tenant.id, { ...reasonPrompt.patch, reason: reason.trim() });
        setReasonPrompt(null);
        setReason("");
    };

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="super-admin-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="eyebrow">SaaS Ops</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Super Admin.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Tenant lifecycle, plan changes and trial management. Customer financial data is NOT
                        accessed from this panel — every action is audit-logged.
                    </p>
                </div>
                <Button
                    onClick={() => setCreateOpen(true)}
                    className="bg-stone-900 hover:bg-stone-800 text-white"
                    data-testid="sa-new-tenant"
                >
                    <Plus size={14} className="mr-1.5" /> New tenant
                </Button>
            </div>

            {/* Snapshot */}
            {loading && !summary ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-stone-100 rounded-2xl animate-pulse" />)}
                </div>
            ) : summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="sa-summary">
                    <MetricCard icon={Building2} label="Total tenants" value={summary.tenants.total} sub={`${summary.tenants.active} active`} />
                    <MetricCard icon={TrendingUp} label="MRR (₹)" value={fmtMoney(summary.revenue.mrr_inr)} sub={`ARR ${fmtMoney(summary.revenue.arr_inr)}`} />
                    <MetricCard icon={Clock} label="Trials ending in 7d" value={summary.revenue.trial_expiring_7d} sub={`${summary.tenants.trial} in trial`} tone="amber" />
                    <MetricCard icon={HardDrive} label="Storage used" value={`${summary.usage.storage_mb} MB`} sub="across all tenants" />
                </div>
            )}

            {/* Tenant table */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <table className="dense-table w-full">
                    <thead>
                        <tr>
                            <th>Tenant</th>
                            <th>Type · Admin</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th>Trial ends</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-stone-500 py-8">No tenants yet.</td></tr>
                        ) : rows.map((t) => {
                            const tone = STATUS_TONE[t.status] || STATUS_TONE.cancelled;
                            const isDefault = t.id === "default";
                            return (
                                <tr key={t.id} data-testid={`sa-tenant-${t.id}`}>
                                    <td>
                                        <div className="font-medium">
                                            {t.name}
                                            {isDefault && <span className="ml-2 text-[10px] text-stone-500 uppercase font-semibold">default</span>}
                                        </div>
                                        <div className="text-[11px] text-stone-500">Created {t.created_at?.slice(0, 10)}</div>
                                    </td>
                                    <td>
                                        <div className="text-sm">{t.type}</div>
                                        <div className="text-[11px] text-stone-500 flex items-center gap-1"><Mail size={10} />{t.admin_email}</div>
                                    </td>
                                    <td>
                                        <Select value={t.plan} onValueChange={(v) => applyUpdate(t.id, { plan: v })}>
                                            <SelectTrigger className="h-7 w-32 text-xs" data-testid={`sa-plan-${t.id}`}>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {plans.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td>
                                        <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${tone}`}>
                                            {t.status}
                                        </span>
                                    </td>
                                    <td className="text-[12px] tabular-nums text-stone-600">
                                        {t.trial_end ? t.trial_end.slice(0, 10) : "—"}
                                    </td>
                                    <td className="text-right">
                                        <div className="inline-flex gap-1">
                                            {t.status === "trial" && (
                                                <button
                                                    onClick={() => applyUpdate(t.id, { trial_extend_days: 7 })}
                                                    className="p-1.5 text-stone-500 hover:text-stone-900"
                                                    title="Extend trial 7 days"
                                                    data-testid={`sa-extend-${t.id}`}
                                                >
                                                    <Clock size={13} />
                                                </button>
                                            )}
                                            {t.status !== "suspended" && !isDefault && (
                                                <button
                                                    onClick={() => setReasonPrompt({ tenant: t, patch: { status: "suspended" }, label: "Suspend tenant" })}
                                                    className="p-1.5 text-stone-500 hover:text-rose-600"
                                                    title="Suspend"
                                                    data-testid={`sa-suspend-${t.id}`}
                                                >
                                                    <Pause size={13} />
                                                </button>
                                            )}
                                            {t.status === "suspended" && (
                                                <button
                                                    onClick={() => applyUpdate(t.id, { status: "active" })}
                                                    className="p-1.5 text-stone-500 hover:text-emerald-600"
                                                    title="Activate"
                                                    data-testid={`sa-activate-${t.id}`}
                                                >
                                                    <PlayCircle size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="sa-create-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Provision a workspace</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="eyebrow">Workspace name</Label>
                                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sa-form-name" />
                            </div>
                            <div>
                                <Label className="eyebrow">Type</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="sa-form-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {["Business", "Family", "CA", "Enterprise"].map((t) => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="eyebrow">Admin name</Label>
                                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="sa-form-admin-name" />
                            </div>
                            <div>
                                <Label className="eyebrow">Admin email</Label>
                                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="sa-form-admin-email" />
                            </div>
                            <div>
                                <Label className="eyebrow">Mobile (optional)</Label>
                                <Input value={form.admin_mobile} onChange={(e) => setForm({ ...form, admin_mobile: e.target.value })} data-testid="sa-form-mobile" />
                            </div>
                            <div>
                                <Label className="eyebrow">Plan</Label>
                                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                                    <SelectTrigger data-testid="sa-form-plan"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {plans.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="eyebrow">Trial days</Label>
                                <Input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: Number(e.target.value) })} data-testid="sa-form-trial" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button onClick={createTenant} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="sa-form-submit">
                            <CheckCircle2 size={14} className="mr-1.5" /> Provision
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reason dialog */}
            <Dialog open={!!reasonPrompt} onOpenChange={(o) => !o && setReasonPrompt(null)}>
                <DialogContent data-testid="sa-reason-dialog">
                    <DialogHeader>
                        <DialogTitle>{reasonPrompt?.label} · {reasonPrompt?.tenant.name}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-stone-500">Reason will be recorded to the audit trail and shown to any admin who reviews this tenant.</p>
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Payment failed; email sent"
                        data-testid="sa-reason-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setReasonPrompt(null); setReason(""); }}>Cancel</Button>
                        <Button onClick={confirmReason} disabled={reason.trim().length < 5} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="sa-reason-confirm">
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function MetricCard({ icon: Icon, label, value, sub, tone }) {
    const toneClass = tone === "amber" ? "text-amber-700 bg-amber-50" : "text-stone-700 bg-stone-100";
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-1">
                <span className={`w-6 h-6 rounded flex items-center justify-center ${toneClass}`}>
                    <Icon size={12} />
                </span>
                <div className="eyebrow">{label}</div>
            </div>
            <div className="text-2xl font-bold tabular-nums text-stone-900">{value}</div>
            {sub && <div className="text-[11px] text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}
